import React, { useCallback, useState } from "react";
import {
  Directions,
  fieldLengthConstants,
  globalConstants,
  modeConstants,
  Regex,
  sportConstants,
  timezoneConstants,
  translationConstants,
} from "../../../constants";
import {
  appendMandatorySignAndTimezoneText,
  getSportDataForDropdown,
  mergeDateTimeToServerFormat,
  UTCWithTimezoneToISOString,
} from "../../../system";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { allSharedComponents } from "../..";
import { Button, Col, Form, Row } from "react-bootstrap";
import ReactDatePickerWrapper from "../ReactDatePickerWrapper/ReactDatePickerWrapper";
import { UserActions } from "../../../redux/actions";
import moment from "moment";
import { ToastifyService } from "../../../services";

const AddQualification = (props) => {
  const { Mandatory_Character } = globalConstants;
  const { InputFieldWrapper, FileUploader } = allSharedComponents;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [boatTypeOptions, setBoatTypeOptions] = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [proficiencyLevelOptions, setProficiencyLevelOptions] = useState([]);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  const {
    setValue,
    getValues,
    register,
    trigger,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const formState = useSelector(
    (state) => state?.UserReducer?.qualificationDocFormState
  );

  const onSubmit = (data) => {
    const { dateOfCompletionString, ...otherData } = data;
    const dataToSend = {
      ...otherData,
      dateOfCompletionString: mergeDateTimeToServerFormat(
        dateOfCompletionString,
        moment().set(timezoneConstants.twelveAM).toDate()
      ),
    };
    // Check if the data already exists in formState
    const isDataPresent = formState.some((item) => {
      return item.boatTypeId === dataToSend.boatTypeId;
    });
    if (isDataPresent) {
      // Show error message if data already exists
      ToastifyService.showError(
        t(translationConstants.QUALIFICATION_ALREADY_EXIST)
      );
      return; // Exit the function to prevent further actions
    }
    const updatedFormState = [...formState, dataToSend];
    dispatch(UserActions.setQualificationFormState(updatedFormState));
    props?.setAddQualificationModal(false);
  };

  const handleFileUpload = useCallback(
    (file, key) => {
      setValue(key, file, { shouldValidate: true });
    },
    [setValue]
  );

  return (
    <section>
      <div className="ViewHeader">
        <p className="viewUserPara">
          {t(translationConstants.ADD_QUALIFICATION)}
        </p>
      </div>
      <p className="horizentalLine" />
      <div className="m-t-24">
        <Form noValidate className="ViewUserContent">
          <Row>
            <InputFieldWrapper
              label={
                `${t(translationConstants.SPORT_TYPE)}:` + Mandatory_Character
              }
            >
              <Form.Select
                id="sportId"
                {...register("sportId", {
                  required: t(translationConstants.SPORT_TYPE_IS_REQUIRED),
                })}
                onChange={(event) => {
                  setBoatTypeOptions(
                    getSportDataForDropdown(t, event.target.value)
                  );
                  setValue("sportId", event.target.value);
                  trigger("sportId");
                }}
              >
                <option value="">
                  {t(translationConstants.SELECT_SPORT_TYPE)}
                </option>
                {Object.entries(sportConstants).map(([sport, value]) => (
                  <option key={value} value={value}>
                    {t(translationConstants[sport])}
                  </option>
                ))}
              </Form.Select>
              {errors?.sportId && (
                <p className="errorText">{errors?.sportId.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                `${t(translationConstants.BOAT_TYPE)}:` + Mandatory_Character
              }
            >
              <Form.Select
                id="boatTypeId"
                {...register("boatTypeId", {
                  required: t(translationConstants.BOAT_TYPE_REQUIRED),
                })}
                onChange={(event) => {
                  setLevelOptions(
                    getSportDataForDropdown(
                      t,
                      getValues("sportId"),
                      event.target.value
                    )
                  );
                  setValue("boatTypeId", event.target.value);
                  trigger("boatTypeId");
                }}
              >
                <option value="">
                  {t(translationConstants.SELECT_BOAT_TYPE)}
                </option>
                {boatTypeOptions.map((object) => (
                  <option key={object.value} value={object.value}>
                    {t(translationConstants[object.label])}
                  </option>
                ))}
              </Form.Select>
              {errors?.boatTypeId && (
                <p className="errorText">{errors?.boatTypeId.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={appendMandatorySignAndTimezoneText(
                t(translationConstants.DATE_OF_COMPLETION),
                t
              )}
            >
              <Controller
                className="cursor-pointer"
                name="dateOfCompletionString"
                control={control}
                rules={{
                  required: t(translationConstants.DATE_OF_COMPLETION_REQUIRED),
                }}
                render={({ field }) => (
                  <ReactDatePickerWrapper
                    onChange={(date) => {
                      field.onChange(date);
                      trigger("dateOfCompletionString");
                    }}
                    selected={field.value}
                    maxDate={new Date(UTCWithTimezoneToISOString())}
                    value={field.value}
                  />
                )}
              />
              {errors?.dateOfCompletionString && (
                <p className="errorText">
                  {errors?.dateOfCompletionString.message}
                </p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                `${t(translationConstants.SUGGESTED_LEVEL)}:` +
                Mandatory_Character
              }
            >
              <Form.Select
                id="levelId"
                {...register("levelId", {
                  required: t(translationConstants.SUGGESTED_LEVEL_REQUIRED),
                })}
                onChange={(event) => {
                  setProficiencyLevelOptions(
                    getSportDataForDropdown(
                      t,
                      getValues("sportId"),
                      getValues("boatTypeId"),
                      event.target.value
                    )
                  );
                  setValue("levelId", event.target.value);
                  trigger("levelId");
                }}
              >
                <option value="">{t(translationConstants.SELECT_LEVEL)}</option>
                {levelOptions.map((object) => (
                  <option key={object.value} value={object.value}>
                    {t(translationConstants[object.label])}
                  </option>
                ))}
              </Form.Select>
              {errors?.levelId && (
                <p className="errorText">{errors?.levelId.message}</p>
              )}
            </InputFieldWrapper>

            {!props?.isInstructor && (
              <InputFieldWrapper
                label={
                  `${t(translationConstants.SUGGESTED_PROFICIENCY_LEVEL)}:` +
                  Mandatory_Character
                }
              >
                <Form.Select
                  id="proficiencyLevelId"
                  {...register("proficiencyLevelId", {
                    required: t(
                      translationConstants.SUGGESTED_PROFICIENCY_LEVEL_REQUIRED
                    ),
                  })}
                  onChange={(event) => {
                    setValue("proficiencyLevelId", event.target.value);
                    trigger("proficiencyLevelId");
                  }}
                >
                  <option value="">
                    {t(translationConstants.SELECT_PROFICIENCY_LEVEL)}
                  </option>
                  {proficiencyLevelOptions.map((object) => (
                    <option key={object.value} value={object.value}>
                      {t(translationConstants[object.label])}
                    </option>
                  ))}
                </Form.Select>
                {errors?.proficiencyLevelId && (
                  <p className="errorText">
                    {errors?.proficiencyLevelId.message}
                  </p>
                )}
              </InputFieldWrapper>
            )}

            <InputFieldWrapper
              lg={6}
              md={12}
              label={
                `${t(translationConstants.ATTACHMENTS)}:` + Mandatory_Character
              }
            >
              <FileUploader
                sm
                {...register("qualifications", {
                  required: t(translationConstants.imageRequired),
                })}
                onFileUpload={(file) =>
                  handleFileUpload(file, "qualifications")
                }
                setValidationErrorMessage={setValidationErrorMessage}
                isError={errors.qualifications || validationErrorMessage}
              />
              {errors.qualifications && !validationErrorMessage && (
                <p className="errorText">{errors.qualifications.message}</p>
              )}
              {validationErrorMessage && (
                <p className="errorText">{validationErrorMessage}</p>
              )}
            </InputFieldWrapper>
          </Row>

          <Row className="mt-4">
            <Col className="mx-1 d-flex justify-content-center">
              <Button
                className="btn-submit mb-2"
                type="button"
                onClick={handleSubmit(onSubmit)}
              >
                {t(translationConstants.submit)}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </section>
  );
};

export default AddQualification;
